import {
  type PointerEvent as ReactPointerEvent,
  useCallback,
  useEffect,
  useRef,
  useState
} from 'react';

export type DrawerSide = 'bottom' | 'left' | 'right';

// Side drawers match the fixed 35rem width drawers have always had.
export const DEFAULT_DRAWER_SIZES: Record<DrawerSide, number> = {
  bottom: 360,
  left: 560,
  right: 560
};
export const MIN_DRAWER_SIZE = 160;
// Room kept between the drawer and the far edge of the window so its header stays reachable.
const WINDOW_GAP = 80;

const windowExtent = (side: DrawerSide) =>
  side === 'bottom' ? window.innerHeight : window.innerWidth;

// How far the pointer is from the edge the drawer is anchored to.
const pointerDistance = (side: DrawerSide, event: PointerEvent) => {
  if (side === 'bottom') return window.innerHeight - event.clientY;
  if (side === 'right') return window.innerWidth - event.clientX;
  return event.clientX;
};

export const maxDrawerSize = (extent: number, minSize: number = MIN_DRAWER_SIZE) =>
  Math.max(minSize, extent - WINDOW_GAP);

export const clampDrawerSize = (size: number, extent: number, minSize: number = MIN_DRAWER_SIZE) =>
  Math.round(Math.min(Math.max(size, minSize), maxDrawerSize(extent, minSize)));

export interface DrawerSizeOptions {
  side: DrawerSide;
  defaultSize?: number;
  minSize?: number;
  /** localStorage key that remembers the chosen size between visits. */
  storageKey?: string;
}

const loadSize = (storageKey: string | undefined, fallback: number) => {
  if (!storageKey) return fallback;
  try {
    return Number(localStorage.getItem(storageKey)) || fallback;
  } catch {
    return fallback;
  }
};

const saveSize = (storageKey: string | undefined, size: number) => {
  if (!storageKey) return;
  try {
    localStorage.setItem(storageKey, String(size));
  } catch {
    // The size just resets on reload when storage is unavailable.
  }
};

// The chosen size is kept apart from the size shown, so shrinking the window clamps the drawer
// for now without overwriting what the user picked.
const useDrawerSize = ({
  side,
  defaultSize,
  minSize = MIN_DRAWER_SIZE,
  storageKey
}: DrawerSizeOptions) => {
  const [preferred, setPreferred] = useState(() =>
    loadSize(storageKey, defaultSize ?? DEFAULT_DRAWER_SIZES[side])
  );
  const preferredRef = useRef(preferred);
  const [extent, setExtent] = useState(() => windowExtent(side));
  const stopDragging = useRef<(() => void) | null>(null);

  useEffect(() => {
    const handleResize = () => setExtent(windowExtent(side));
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      stopDragging.current?.();
    };
  }, [side]);

  const clamp = useCallback(
    (size: number) => clampDrawerSize(size, windowExtent(side), minSize),
    [minSize, side]
  );

  const choose = useCallback((size: number) => {
    preferredRef.current = size;
    setPreferred(size);
  }, []);

  const startResize = useCallback(
    (event: ReactPointerEvent) => {
      // preventDefault stops text selection during the drag but also skips the usual focus, so
      // the handle is focused by hand to keep the arrow keys working after a click or drag.
      event.preventDefault();
      (event.currentTarget as HTMLElement | null)?.focus?.();
      let latest: number | null = null;
      const handleMove = (move: PointerEvent) => {
        latest = clamp(pointerDistance(side, move));
        choose(latest);
      };
      const stop = () => {
        window.removeEventListener('pointermove', handleMove);
        window.removeEventListener('pointerup', stop);
        window.removeEventListener('pointercancel', stop);
        stopDragging.current = null;
        if (latest !== null) saveSize(storageKey, latest);
      };
      stopDragging.current?.();
      stopDragging.current = stop;
      window.addEventListener('pointermove', handleMove);
      window.addEventListener('pointerup', stop);
      window.addEventListener('pointercancel', stop);
    },
    [choose, clamp, side, storageKey]
  );

  /** Grows the drawer by `delta` pixels, or shrinks it when negative. */
  const nudge = useCallback(
    (delta: number) => {
      const next = clamp(clamp(preferredRef.current) + delta);
      choose(next);
      saveSize(storageKey, next);
    },
    [choose, clamp, storageKey]
  );

  return {
    maxSize: maxDrawerSize(extent, minSize),
    minSize,
    nudge,
    size: clampDrawerSize(preferred, extent, minSize),
    startResize
  };
};

export default useDrawerSize;
